import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Container, Form, Image, Spinner } from 'react-bootstrap';
import { Formik, Field, FieldArray, FormikProps } from 'formik';
import * as Yup from 'yup';
import Delete from "../../../../assets/images/delete-vector.png";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
    updateMaterialRequestAsync, getAMaterialRequestAsync, setMaterialRequestId
} from '../../../../slices/requests/requestsSlice';
import { RootState } from "../../../../store/store";
import { Select, FormControl, InputLabel, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import CurrencyInput, { currencyMask } from '../../../../components/form/currency-input';


interface Material {
    material_id: string;
    quantity: string;
    unit_cost: string;
}

interface EditMaterialRequestValues {
    company_id: string;
    vendor_id: string;
    materials: {
        material_id: string;
        quantity: string;
        unit_cost: string;
    }[];
    projectId: string;
    id: string;
}
interface EditMaterialRequestProps {
    closeModal: () => void;
    show: boolean;
    materialRequestId: string;
    onEditSuccess: () => void;
    projectId: string;
}

const EditMaterialRequest: React.FC<EditMaterialRequestProps> = ({ closeModal, show, onEditSuccess, projectId, materialRequestId }) => {
    const dispatch = useAppDispatch();
    const companyId = useAppSelector(
        (state: RootState) => state.auth.user?.company_id || "",
    );
    const vendors = useAppSelector((state) => state.settings.vendors?.data);
    const materials = useAppSelector((state) => state.settings.materials?.data);
    const materialRequestData = useAppSelector((state: any) => state.requests.materialRequestData);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (materialRequestId) {
            dispatch(setMaterialRequestId(materialRequestId));
            console.log("Fetching labour request with ID:", materialRequestId);
            dispatch(getAMaterialRequestAsync({ companyId, id: materialRequestId, projectId })).then(() =>
                setLoading(false),
            );
        } else {
            console.log("materialRequestId is not set");
        }
    }, [dispatch, companyId, materialRequestId, projectId]);


    if (loading) {
        return null;
    }


    return (
        <Container fluid className=''>
            <Modal centered size="lg" show={show} onHide={closeModal} style={{ maxHeight: "100vh", overflowY: "auto" }}>
                <div className='px-5'>
                    <Formik
                        initialValues={{
                            company_id: companyId || '',
                            vendor_id: materialRequestData?.material_requests?.vendor_id || '',
                            materials: materialRequestData?.material_requests?.materials.map((material: Material) => ({
                                material_id: material.material_id,
                                quantity: material.quantity || '',
                                unit_cost: material.unit_cost || '',
                            })) || [{ material_id: '', quantity: '', unit_cost: '' }],
                            projectId: projectId || '',
                            id: materialRequestData?.material_requests?.id || '',

                        }}
                        validationSchema={Yup.object().shape({
                            vendor_id: Yup.string().required('Required'),
                            materials: Yup.array().of(
                                Yup.object().shape({
                                    material_id: Yup.string().required('Required'),
                                    quantity: Yup.number().transform((value) => (isNaN(value) ? 0 : value)).required('Required'), // Transform string to number
                                    unit_cost: Yup.number().transform((value) => (isNaN(value) ? 0 : value)).required('Required'), // Same for unit_cost
                                })
                            ),
                        })}
                        onSubmit={(values: EditMaterialRequestValues, { setSubmitting }) => {
                            console.log("Formik values: ", values);
                            const flattenedValues = {
                                company_id: companyId,
                                vendor_id: values.vendor_id,
                                projectId: projectId ?? '',
                                materials: values.materials.map((material) => ({
                                    material_id: material.material_id,
                                    quantity: material.quantity,
                                    unit_cost: parseFloat(material.unit_cost.replace(/[^0-9.-]+/g, '')).toString() || '0',
                                })),
                                id: materialRequestId || "",
                            };
                            const requestData = {
                                updateMaterialRequestData: flattenedValues, // Required by the action
                                id: materialRequestId || "",
                            };
                            const addSpacesToMessage = (message: any) => {
                                return message.replace(/([a-z])([A-Z])/g, '$1 $2');
                            };
                            dispatch(
                                updateMaterialRequestAsync(requestData)
                            ).unwrap()
                                .then((response) => {
                                    toast.success("Material request updated successfully");
                                    onEditSuccess();
                                    closeModal();
                                })
                                .catch((error) => {
                                    const errorMessage = error.message ? addSpacesToMessage(error.message) : "An unexpected error occurred";
                                    toast.error(errorMessage);
                                })
                                .finally(() => {
                                    setSubmitting(false);
                                });
                        }}

                    >
                        {({ errors, touched, handleSubmit, isSubmitting, values, handleChange, handleBlur }: FormikProps<EditMaterialRequestValues>) => (
                            <Form onSubmit={handleSubmit}>
                                <Modal.Header closeButton className='text-start mt-2'>
                                    <h4 className="confirm-header">Edit material request</h4>
                                </Modal.Header>
                                <Modal.Body>
                                    <Row>
                                        <Col md={12} className='mt-2'>
                                            <label className='mt-2'>
                                                <p>Vendor name {" "}
                                                    <span className="text-danger">*</span>
                                                </p>
                                            </label>
                                            <FormControl fullWidth>
                                                <InputLabel>Select vendor</InputLabel>
                                                <Select
                                                    label="Select vendor"
                                                    name="vendor_id"
                                                    value={values.vendor_id}
                                                    onChange={handleChange}
                                                >
                                                    {vendors?.map((vendor) => (
                                                        <MenuItem key={vendor.id} value={vendor.id}>
                                                            {vendor.vendor_name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Col>
                                    </Row>
                                    <FieldArray name="materials">
                                        {({ push, remove }) => (
                                            <>
                                                {values.materials.map((_, index: any) => (
                                                    <Row key={index}>
                                                        <Col md={4} className='mt-2'>
                                                            <label className='mt-2'>
                                                                <p>Material {" "}
                                                                    <span className="text-danger">*</span>
                                                                </p>
                                                            </label>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Select material</InputLabel>
                                                                <Select
                                                                    label="Select material"
                                                                    name={`materials[${index}].material_id`}
                                                                    value={values.materials[index].material_id || ''}
                                                                    onChange={(event) => handleChange(event)}
                                                                >
                                                                    {materials?.map((material) => (
                                                                        <MenuItem key={material.id} value={material.id}>
                                                                            {material.material_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>

                                                        </Col>
                                                        <Col md={4} className='mt-2'>
                                                            <label className='mt-2'>
                                                                <p>Quantity {" "}
                                                                    <span className="text-danger">*</span>
                                                                </p>
                                                            </label>
                                                            <Field
                                                                type='number'
                                                                name={`materials[${index}].quantity`}
                                                                className={`fluent-input  ${touched.materials?.[index]?.quantity && errors.materials?.[index]?.toString ? 'is-invalid' : ''}`}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={3} className='mt-2'>
                                                            <label className='mt-2'>
                                                                <p>Cost {" "}
                                                                    <span className="text-danger">*</span>
                                                                </p>
                                                            </label>

                                                            <CurrencyInput
                                                                name={`materials[${index}].unit_cost`}
                                                                placeholder="Enter amount"
                                                                className={`fluent-input  ${touched.materials?.[index]?.unit_cost && errors.materials?.[index].toString ? 'is-invalid' : ''}`}
                                                                value={values.materials[index].unit_cost}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const maskedValue = currencyMask(e);
                                                                    const numericValue = maskedValue.target.value;
                                                                    handleChange({
                                                                        target: {
                                                                            name: `materials[${index}].unit_cost`,
                                                                            value: numericValue || '',
                                                                        },
                                                                    });
                                                                }}
                                                                onBlur={handleBlur}
                                                            />
                                                        </Col>
                                                        <Col md={1} className='mt-5'>
                                                            {index > 0 && (
                                                                <Button type="button" onClick={() => remove(index)} className='border-0 mt-3 bg-color-4'>
                                                                    <Image src={Delete} width={30} height={30} />
                                                                </Button>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Row>
                                                    <Col>
                                                        <div className='mt-3'>
                                                            <Button type='button' onClick={() => push({ material_id: '', quantity: '', unit_cost: '' })} className='border-0 add-more-btn'>
                                                                <span className="text-center">Add </span>
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </FieldArray>
                                </Modal.Body>
                                <Modal.Footer className='mt-5'>
                                    <Row >
                                        <Col md={6}>
                                            <div className=' mt-1'>
                                                <Button onClick={closeModal} className='cancel-btn'>
                                                    <span className="text-center">Cancel </span>
                                                </Button>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className=' mt-1'>
                                                <Button type='submit' className=' border-0 create-btn bg-color-1' onClick={() => console.log('Edit request button clicked')}>
                                                    {isSubmitting ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <span className="text-center">Edit request</span>
                                                    )}                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Modal.Footer>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Modal>
        </Container>
    );
};

export default EditMaterialRequest;
